"use client";

import { MeetingMinutesPanel } from "@/components/meeting-minutes-panel";
import { SubPageLayout } from "@/components/sub-page-layout";
import { Button } from "@/components/ui/button";
import { MicIcon } from "@/components/ui/mic";
import type { DbModelTier } from "@/types/db";
import { MicOffIcon } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";

type RecordingKey = { id: string; project: boolean };
type ActiveRecordingKey = RecordingKey & { muted: boolean };

type HostedMeetingPanelProps = {
  recording: RecordingKey;
  anotherRecordingActive: boolean;
  isVisible: boolean;
  workflowsAllowedModelTiers: DbModelTier[] | null;
  codeSpacesAllowedModelTiers: DbModelTier[] | null;
  onRecordingStateChange: (recording: RecordingKey, isRecording: boolean, isMuted: boolean) => void;
};

function HostedMeetingPanel({
  recording,
  anotherRecordingActive,
  isVisible,
  workflowsAllowedModelTiers,
  codeSpacesAllowedModelTiers,
  onRecordingStateChange,
}: HostedMeetingPanelProps) {
  const router = useRouter();
  const { id, project } = recording;
  const handleRecordingStateChange = useCallback(
    (isRecording: boolean, isMuted: boolean) => onRecordingStateChange({ id, project }, isRecording, isMuted),
    [id, project, onRecordingStateChange]
  );

  return (
    <SubPageLayout
      workflowsAllowedModelTiers={workflowsAllowedModelTiers}
      codeSpacesAllowedModelTiers={codeSpacesAllowedModelTiers}
    >
      <MeetingMinutesPanel
        recordingId={project ? null : id}
        projectRecordingId={project ? id : null}
        anotherRecordingActive={anotherRecordingActive}
        isVisible={isVisible}
        onRecordingStateChange={handleRecordingStateChange}
        onBack={() => router.push("/")}
      />
    </SubPageLayout>
  );
}

function sameRecording(a: RecordingKey | null, b: RecordingKey | null) {
  return !!a && !!b && a.id === b.id && a.project === b.project;
}

function recordingUrl(recording: RecordingKey) {
  return `/meeting-minutes?${recording.project ? "projectRecordingId" : "recordingId"}=${encodeURIComponent(recording.id)}`;
}

export function ActiveMeetingRecording({
  workflowsAllowedModelTiers,
  codeSpacesAllowedModelTiers,
}: {
  workflowsAllowedModelTiers: DbModelTier[] | null;
  codeSpacesAllowedModelTiers: DbModelTier[] | null;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const onMeetingPage = pathname === "/meeting-minutes";
  const projectRecordingId = onMeetingPage ? searchParams.get("projectRecordingId") : null;
  const recordingId = onMeetingPage ? searchParams.get("recordingId") : null;
  const current: RecordingKey | null = projectRecordingId
    ? { id: projectRecordingId, project: true }
    : recordingId
      ? { id: recordingId, project: false }
      : null;
  const [active, setActive] = useState<ActiveRecordingKey | null>(null);
  const primary = active ?? current;
  const showPrimary = onMeetingPage && sameRecording(primary, current);
  const showOther = onMeetingPage && !!current && !!primary && !sameRecording(current, primary);

  const handleRecordingStateChange = useCallback((recording: RecordingKey, isRecording: boolean, isMuted: boolean) => {
    setActive((previous) => {
      if (!isRecording) return sameRecording(previous, recording) ? null : previous;
      if (sameRecording(previous, recording) && previous?.muted === isMuted) return previous;
      return { ...recording, muted: isMuted };
    });
  }, [setActive]);

  return (
    <>
      {primary ? (
        <div
          aria-hidden={!showPrimary}
          className={showPrimary ? "fixed inset-0 z-40" : "hidden"}
          inert={!showPrimary}
          key={`${primary.project ? "project" : "personal"}:${primary.id}`}
        >
          <HostedMeetingPanel
            recording={primary}
            anotherRecordingActive={false}
            isVisible={showPrimary}
            workflowsAllowedModelTiers={workflowsAllowedModelTiers}
            codeSpacesAllowedModelTiers={codeSpacesAllowedModelTiers}
            onRecordingStateChange={handleRecordingStateChange}
          />
        </div>
      ) : null}
      {showOther && current ? (
        <div className="fixed inset-0 z-40" key={`${current.project ? "project" : "personal"}:${current.id}`}>
          <HostedMeetingPanel
            recording={current}
            anotherRecordingActive
            isVisible
            workflowsAllowedModelTiers={workflowsAllowedModelTiers}
            codeSpacesAllowedModelTiers={codeSpacesAllowedModelTiers}
            onRecordingStateChange={handleRecordingStateChange}
          />
        </div>
      ) : null}
      {onMeetingPage && !current ? (
        <div className="fixed inset-0 z-40">
          <SubPageLayout
            workflowsAllowedModelTiers={workflowsAllowedModelTiers}
            codeSpacesAllowedModelTiers={codeSpacesAllowedModelTiers}
          >
            <MeetingMinutesPanel onBack={() => router.push("/")} anotherRecordingActive={!!active} />
          </SubPageLayout>
        </div>
      ) : null}
      {active && !showPrimary ? (
        <Button
          className="fixed bottom-4 right-4 z-50 gap-2 rounded-full shadow-lg"
          onClick={() => router.push(recordingUrl(active))}
          type="button"
        >
          {active.muted ? <MicOffIcon className="size-4" /> : <MicIcon size={16} />}
          {active.muted ? "Muted · Return to recording" : "Recording · Return to meeting"}
        </Button>
      ) : null}
    </>
  );
}
